import { Flex, Grid, Image, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// import webPhoneKeypadImageSmall from '~/assets/web-phone-call-screen-small.png'
import webPhoneKeypadImage from '~/assets/web-phone-call-screen.png'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { DropdownMangoButton } from '~/components/shared/Dropdown/DropdownMangoButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { MangoButtonVariant } from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { ExtensionInstance } from '@mangovoice/types'

import { useOnboardingStyle } from './componentStyles/Onboarding.style'

interface OnboardingViewProps {
  onActivateButtonClick: () => void
}
export const OnboardingView = ({
  onActivateButtonClick,
}: OnboardingViewProps) => {
  const { t } = useTranslation()
  const handleWebPhoneActivate = () => {
    onActivateButtonClick()
  }
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { pbxExtensions, setCurrentUserExtension, pbx } = useUserState()
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const styles = useOnboardingStyle()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const contentProps = {
    display: styles.content?.display,
    flexDirection: styles.content?.flexDirection,
    alignItems: styles.content?.alignItems,
  }

  const featuresProps = {
    display: styles.features?.display,
    flexDirection: styles.features?.flexDirection,
    justifyContent: styles.features?.justifyContent,
    alignItems: styles.features?.alignItems,
    gap: styles.features?.gap,
  }

  // TODO the ext related stuff can probably be a separate component. settings needs the same functionality
  const extItems = useMemo(() => {
    return (
      pbxExtensions?.map((ext) => {
        return {
          value: ext,
          content: (
            <MenuListItem
              primaryText={ext.name}
              rightContent={<ExtensionBadge extension={ext.number} />}
            />
          ),
          onSelect: (ext: ExtensionInstance) => {
            track(WebPhoneAnalyticsEvents.ExtSelected)
            setCurrentUserExtension(pbx?.id, ext)
            handleWebPhoneActivate()
          },
        } as MenuItem<ExtensionInstance>
      }) ?? []
    )
  }, [pbxExtensions])

  const checkCanActivatePhone = () => {
    if (pbxExtensions?.length === 1) {
      setCurrentUserExtension(pbx?.id, pbxExtensions[0])
      handleWebPhoneActivate()
    } else if (pbxExtensions?.length === 0) {
      setConfirmModalState({
        title: t('onboardingView.confirmModal.title'),
        message: (
          <Flex
            css={{
              flexDirection: 'column',
              gap: '1.5rem',
            }}
          >
            <Text css={{ fontSize: '1rem', fontWeight: '400' }}>
              {t('onboardingView.confirmModal.message1', {
                pbxName: pbx?.name,
              })}
            </Text>
            <Text css={{ fontSize: '1rem', fontWeight: '400' }}>
              {t('onboardingView.confirmModal.message2')}
            </Text>
          </Flex>
        ),
        confirmText: t('onboardingView.confirmModal.confirmText'),
        closeAfterConfirm: true,
        isDestructive: false,
      })
    }
  }

  return (
    <Grid css={styles.content} {...contentProps}>
      <Flex css={styles.features} {...featuresProps}>
        <Image css={styles.image} src={webPhoneKeypadImage} alt="" />
        <Text css={styles.welcomeText}>{t('onboardingView.welcomeText')}</Text>
        <Text css={styles.bodyText}>{t('onboardingView.bodyText')}</Text>
        <Flex>
          <DropdownMangoButton
            variant={MangoButtonVariant.Default_300}
            items={extItems.length > 1 ? extItems : []}
            label={t('onboardingView.buttonLabel')}
            onButtonClick={checkCanActivatePhone}
            menuOptions={{
              placement: MenuPlacement.BottomCenter,
              width: '23.9375rem',
            }}
          ></DropdownMangoButton>
        </Flex>
      </Flex>
    </Grid>
  )
}
